import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  Home, 
  Server, 
  Shield,
  HardDrive, 
  User, 
  Users,
  Upload,
  History,
  LogOut, 
  Menu, 
  X,
  Bell,
  RotateCcw,
  Calendar,
  Wrench,
  Camera,
  MapPin,
  ChevronDown,
  ChevronRight,
  Grid3x3,
  Video,
  Briefcase,
  FileText,
  ClipboardList,
  Plane,
  MessageSquare,
  HelpCircle,
  Cloud,
  CloudRain,
  CloudSun,
  Sun,
  CloudFog,
  Timer
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { dashboardService } from '../services/dashboardService';
import { ticketsService } from '../services/ticketsService';
import nadgodzinyService from '../services/nadgodzinyService';
import logoImage from '../assets/logo.png';
import toast from 'react-hot-toast';

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 5) return 'Dobranoc';
  if (hour < 12) return 'Dzień dobry';
  if (hour < 18) return 'Dzień dobry';
  return 'Dobry wieczór';
};

const getRoleLabel = (role) => {
  if (role === 'super admin') return 'Administrator';
  if (role === 'admin') return 'Administrator';
  return 'Użytkownik';
};

const getWeatherIcon = (code) => {
  if (code === 0) return Sun;
  if (code >= 1 && code <= 3) return CloudSun;
  if (code >= 45 && code <= 48) return CloudFog;
  if (code >= 51 && code <= 67) return CloudRain;
  if (code >= 80 && code <= 82) return CloudRain;
  return Cloud;
};

const getWeatherLabel = (code) => {
  if (code === 0) return 'Słonecznie';
  if (code === 1 || code === 2) return 'Częściowo słonecznie';
  if (code === 3) return 'Pochmurno';
  if (code >= 45 && code <= 48) return 'Mgła';
  if (code >= 51 && code <= 67) return 'Deszcz';
  if (code >= 71 && code <= 77) return 'Śnieg';
  if (code >= 80 && code <= 82) return 'Przelotne opady';
  return 'Zmienna';
};

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [plannerMenuOpen, setPlannerMenuOpen] = useState(false);
  const [usersMenuOpen, setUsersMenuOpen] = useState(false);
  const [kadryMenuOpen, setKadryMenuOpen] = useState(false);
  const [devicesMenuOpen, setDevicesMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const { user, logout, isImpersonating, stopImpersonating, originalAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notificationsRef = useRef(null);
  const knownUnreadNotificationIdsRef = useRef(null);
  const notificationsSyncedRef = useRef(false);
  const userIdForNotificationsRef = useRef(null);

  const { data: notificationsData, isSuccess: notificationsLoaded } = useQuery(
    'notifications',
    dashboardService.getNotifications,
    { 
      refetchInterval: 5000,
      refetchOnWindowFocus: true,
      refetchOnMount: true
    }
  );

  const { data: ticketsData } = useQuery(
    'tickets-badge',
    ticketsService.getTickets,
    {
      refetchInterval: 60000,
      refetchOnWindowFocus: true,
      staleTime: 30000
    }
  );

  const { data: nadgodzinyLicznik } = useQuery(
    'nadgodziny-licznik',
    nadgodzinyService.getLicznik,
    {
      refetchInterval: 60000,
      refetchOnWindowFocus: true,
      staleTime: 30000
    }
  );

  const { data: weather } = useQuery(
    'weather-widget',
    async () => {
      const res = await fetch(
        'https://api.open-meteo.com/v1/forecast?latitude=52.23&longitude=21.01&current=temperature_2m,weather_code&timezone=Europe%2FWarsaw'
      );
      if (!res.ok) throw new Error('weather');
      const data = await res.json();
      return {
        temp: Math.round(data.current.temperature_2m),
        code: data.current.weather_code
      };
    },
    {
      staleTime: 30 * 60 * 1000,
      refetchInterval: 30 * 60 * 1000,
      retry: 1
    }
  );

  const notifications = notificationsData?.notifications || [];
  const unreadCount = notifications.filter(n => !n.read).length;
  const openTicketsCount = (() => {
    const list = ticketsData?.tickets || [];
    return list.filter((t) => t.status === 'nowy' || t.status === 'w_trakcie').length;
  })();

  useEffect(() => {
    const uid = user?._id || user?.id || null;
    if (userIdForNotificationsRef.current !== uid) {
      userIdForNotificationsRef.current = uid;
      notificationsSyncedRef.current = false;
      knownUnreadNotificationIdsRef.current = null;
    }
  }, [user?._id, user?.id]);

  useEffect(() => {
    if (!notificationsLoaded) return;

    const unreadNotifications = notifications.filter((n) => !n.read);
    const unreadIds = new Set(unreadNotifications.map((n) => n.id));

    if (!notificationsSyncedRef.current) {
      knownUnreadNotificationIdsRef.current = unreadIds;
      notificationsSyncedRef.current = true;
      return;
    }

    const newOnes = unreadNotifications.filter(
      (n) => !knownUnreadNotificationIdsRef.current?.has(n.id)
    );

    if (newOnes.length === 1) {
      toast(newOnes[0].title, { icon: '🔔', duration: 6000 });
    } else if (newOnes.length > 1) {
      const newest = [...newOnes].sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      )[0];
      toast(newest.title, { icon: '🔔', duration: 6000 });
      toast(`Masz ${newOnes.length} nowych powiadomień — sprawdź dzwoneczek`, {
        duration: 5000
      });
    }

    knownUnreadNotificationIdsRef.current = unreadIds;
  }, [notifications, notificationsLoaded]);

  const markAsReadMutation = useMutation(
    dashboardService.markNotificationAsRead,
    {
      onSuccess: () => {
        queryClient.invalidateQueries('notifications');
      }
    }
  );

  const markAllAsReadMutation = useMutation(
    dashboardService.markAllNotificationsAsRead,
    {
      onSuccess: () => {
        queryClient.invalidateQueries('notifications');
        toast.success('Wszystkie powiadomienia zostały oznaczone jako przeczytane');
      }
    }
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setNotificationsOpen(false);
      }
    };

    if (notificationsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [notificationsOpen]);

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsReadMutation.mutate(notification.id);
    }
    
    if (notification.action?.type === 'ticket' && notification.action?.id) {
      navigate(`/tickets?ticket=${notification.action.id}`);
      setNotificationsOpen(false);
    } else if (notification.relatedTicket?.id) {
      navigate(`/tickets?ticket=${notification.relatedTicket.id}`);
      setNotificationsOpen(false);
    } else if (notification.action?.url) {
      navigate(notification.action.url);
      setNotificationsOpen(false);
    }
  };

  const isPlannerSection = location.pathname.startsWith('/planner') && 
    !location.pathname.startsWith('/planner/teams');
  const isUsersSection = location.pathname.startsWith('/users') || 
    location.pathname.startsWith('/planner/teams');
  const isKadrySection = location.pathname.startsWith('/kadry');
  const isDevicesSection = location.pathname.startsWith('/devices') || 
    location.pathname.startsWith('/vpn') || 
    location.pathname.startsWith('/nas');
  
  const plannerMenuClosedRef = React.useRef(false);
  const usersMenuClosedRef = React.useRef(false);
  const kadryMenuClosedRef = React.useRef(false);
  const devicesMenuClosedRef = React.useRef(false);

  React.useEffect(() => {
    if (isPlannerSection && !plannerMenuClosedRef.current) {
      setPlannerMenuOpen(true);
    }
  }, [isPlannerSection]);
  
  React.useEffect(() => {
    if (isUsersSection && !usersMenuClosedRef.current) {
      setUsersMenuOpen(true);
    }
  }, [isUsersSection]);
  
  React.useEffect(() => {
    if (isKadrySection && !kadryMenuClosedRef.current) {
      setKadryMenuOpen(true);
    }
  }, [isKadrySection]);

  React.useEffect(() => {
    if (isDevicesSection && !devicesMenuClosedRef.current) {
      setDevicesMenuOpen(true);
    }
  }, [isDevicesSection]);

  // Zamknij mobilny sidebar po zmianie ścieżki
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const navigation = [
    { name: 'Pulpit', href: '/dashboard', icon: Home },
    { 
      name: 'Urządzenia', 
      href: '/devices', 
      icon: Server, 
      superAdminOnly: true,
      submenu: [
        { name: 'Urządzenia', href: '/devices', icon: Server, superAdminOnly: true },
        { name: 'VPN', href: '/vpn', icon: Shield, superAdminOnly: true },
        { name: 'NAS', href: '/nas', icon: HardDrive, superAdminOnly: true }
      ]
    },
    { name: 'CCTV', href: '/cctv', icon: Video, moduleAccess: 'cctv' },
    { name: 'Użytkownicy', href: '/users', icon: Users, superAdminOnly: false, adminAccess: true,
      submenu: [
        { name: 'Użytkownicy', href: '/users', icon: Users },
        { name: 'Aktywność kont', href: '/users/aktywnosc', icon: ClipboardList },
        { name: 'Ekipa Serwisowa', href: '/planner/teams', icon: Wrench, moduleAccess: 'ekipaSerwisowa' }
      ]
    },
    { 
      name: 'Planer', 
      href: '/planner/calendar', 
      icon: Calendar, 
      moduleAccess: 'planner',
      submenu: [
        { name: 'Kalendarz', href: '/planner/calendar', icon: Calendar, moduleAccess: 'planner' },
        { name: 'Kanban', href: '/planner/kanban', icon: Grid3x3, moduleAccess: 'kanban' },
        { name: 'MAPA', href: '/planner/map', icon: MapPin, moduleAccess: 'mapa' },
        { name: 'Farmy Foto', href: '/planner/farms', icon: Camera, moduleAccess: 'farmyFoto' }
      ]
    },
    { name: 'Upload zdjęć', href: '/upload', icon: Upload, moduleAccess: 'upload' },
    { name: 'Historia', href: '/history', icon: History, moduleAccess: 'history' },
    { name: 'Dokumenty', href: '/dokumenty', icon: FileText, moduleAccess: 'dokumenty' },
    { name: 'Ogłoszenia', href: '/announcements', icon: MessageSquare },
    { name: 'Zgłoszenia', href: '/tickets', icon: HelpCircle, badge: openTicketsCount },
    { name: 'Nadgodziny', href: '/nadgodziny', icon: Timer, badge: nadgodzinyLicznik?.doDecyzji || 0 },
    { 
      name: 'Kadry', 
      href: '/kadry', 
      icon: Briefcase, 
      moduleAccessAny: ['urlopy', 'delegacje', 'kadry'],
      submenu: [
        { name: 'Pracownicy', href: '/kadry/pracownicy', icon: Users, moduleAccessAny: ['urlopy', 'delegacje', 'kadry'], kadryHROnly: true },
        { name: 'Dokumenty', href: '/kadry/dokumenty', icon: FileText, moduleAccessAny: ['urlopy', 'delegacje', 'kadry'] },
        { name: 'Delegacje', href: '/kadry/wnioski-delegacyjne', icon: Plane, moduleAccessAny: ['delegacje', 'kadry'] },
        { name: 'Urlopy', href: '/kadry/wnioski', icon: FileText, moduleAccessAny: ['urlopy', 'kadry'] }
      ]
    },
    { name: 'Profil', href: '/profile', icon: User },
  ];

  const isCurrentPath = (path) => {
    return location.pathname === path;
  };

  const getPageMeta = (pathname) => {
    const meta = {
      '/dashboard': { title: null, subtitle: 'Oto podsumowanie Twojego systemu' },
      '/devices': { title: 'Urządzenia', subtitle: 'Zarządzanie urządzeniami' },
      '/vpn': { title: 'VPN', subtitle: 'Połączenia VPN' },
      '/nas': { title: 'NAS', subtitle: 'Serwery NAS' },
      '/cctv': { title: 'CCTV', subtitle: 'Podgląd kamer' },
      '/users': { title: 'Użytkownicy', subtitle: 'Zarządzanie użytkownikami' },
      '/users/aktywnosc': { title: 'Aktywność kont', subtitle: 'Logowania, sesje i zmiany danych' },
      '/planner': { title: 'Planer', subtitle: 'Działania serwisowe' },
      '/planner/calendar': { title: 'Kalendarz', subtitle: 'Planer działań serwisowych' },
      '/planner/kanban': { title: 'Kanban', subtitle: 'Tablica zadań' },
      '/planner/map': { title: 'Mapa', subtitle: 'Mapa farm' },
      '/planner/farms': { title: 'Farmy Foto', subtitle: 'Zdjęcia farm' },
      '/planner/teams': { title: 'Ekipa Serwisowa', subtitle: 'Zespoły serwisowe' },
      '/upload': { title: 'Upload zdjęć', subtitle: 'Przesyłanie zdjęć' },
      '/history': { title: 'Historia', subtitle: 'Historia uploadów' },
      '/kadry': { title: 'Kadry', subtitle: 'Moduł kadrowy' },
      '/kadry/pracownicy': { title: 'Pracownicy', subtitle: 'Lista pracowników' },
      '/kadry/dokumenty': { title: 'Dokumenty', subtitle: 'Dokumenty kadrowe' },
      '/kadry/dokumenty/wniosek-urlopowy': { title: 'Wniosek urlopowy', subtitle: 'Kadry' },
      '/kadry/dokumenty/wniosek-delegacyjny': { title: 'Wniosek delegacyjny', subtitle: 'Kadry' },
      '/kadry/wnioski-delegacyjne': { title: 'Delegacje', subtitle: 'Wnioski delegacyjne' },
      '/kadry/wnioski': { title: 'Urlopy', subtitle: 'Wnioski urlopowe' },
      '/nadgodziny': { title: 'Nadgodziny', subtitle: 'Wnioski o zgodę na pracę w godzinach nadliczbowych' },
      '/nadgodziny/wniosek': { title: 'Wniosek o nadgodziny', subtitle: 'Nadgodziny' },
      '/announcements': { title: 'Ogłoszenia', subtitle: 'Ogłoszenia firmowe' },
      '/tickets': { title: 'Zgłoszenia', subtitle: 'Zgłoszenia i wsparcie' },
      '/profile': { title: 'Profil', subtitle: 'Twoje konto' },
      '/dokumenty': { title: 'Dokumenty', subtitle: 'Moduł dokumentów' },
      '/email-config': { title: 'Email', subtitle: 'Konfiguracja poczty' }
    };
    return meta[pathname] || { title: 'Omega Operations', subtitle: pathname };
  };

  const pageMeta = getPageMeta(location.pathname);

  const displayName = user?.firstName && user?.lastName 
    ? `${user.firstName} ${user.lastName}` 
    : user?.username || 'Użytkownik';
  const firstName = user?.firstName || user?.username || 'Użytkowniku';
  const initials = (
    (user?.firstName?.charAt(0) || '') + (user?.lastName?.charAt(0) || '') ||
    user?.username?.charAt(0) ||
    'U'
  ).toUpperCase().slice(0, 2);

  const headerTitle = location.pathname === '/dashboard'
    ? `${getGreeting()}, ${firstName}`
    : pageMeta.title;

  const WeatherIcon = weather ? getWeatherIcon(weather.code) : Cloud;
  const sidebarProps = {
    navigation,
    isCurrentPath,
    pathname: location.pathname,
    user,
    logout,
    isImpersonating,
    stopImpersonating,
    originalAdmin,
    plannerMenuOpen,
    setPlannerMenuOpen,
    usersMenuOpen,
    setUsersMenuOpen,
    kadryMenuOpen,
    setKadryMenuOpen,
    devicesMenuOpen,
    setDevicesMenuOpen,
    plannerMenuClosedRef,
    usersMenuClosedRef,
    kadryMenuClosedRef,
    devicesMenuClosedRef,
    displayName,
    initials
  };

  return (
    <div className="min-h-screen bg-surface">
      {/* Mobile menu button */}
      <button
        type="button"
        className="lg:hidden fixed top-4 left-4 z-50 p-2.5 rounded-xl bg-sidebar text-white shadow-soft print:hidden"
        onClick={() => setSidebarOpen(true)}
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Mobile sidebar overlay */}
      <div className={`fixed inset-0 z-40 lg:hidden print:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
        <div className="relative flex-1 flex flex-col max-w-[280px] w-full sidebar-topo h-full shadow-2xl">
          <button
            type="button"
            className="absolute top-4 right-4 p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </button>
          <SidebarContent {...sidebarProps} />
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:fixed lg:inset-y-0 lg:left-0 lg:w-[260px] lg:z-30 print:hidden">
        <div className="flex flex-col w-full sidebar-topo border-r border-sidebar-border">
          <SidebarContent {...sidebarProps} />
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-[260px] print:pl-0 min-h-screen flex flex-col">
        {/* Top bar in content area */}
        <header className="sticky top-0 z-20 bg-surface/90 backdrop-blur-md border-b border-black/[0.04] print:hidden">
          <div className="px-4 sm:px-5 lg:px-6 py-4 sm:py-5">
            <div className="flex items-start sm:items-center justify-between gap-4">
              <div className="min-w-0 pl-10 lg:pl-0">
                <h1 className="page-title truncate">
                  {headerTitle}
                </h1>
                <p className="page-subtitle truncate">
                  {pageMeta.subtitle}
                </p>
              </div>

              <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                {isImpersonating && (
                  <button
                    onClick={async () => {
                      const result = await stopImpersonating();
                      if (result.success) {
                        window.location.href = '/dashboard';
                      }
                    }}
                    className="hidden sm:flex px-3 py-1.5 bg-orange-500 text-white text-xs font-semibold rounded-xl hover:bg-orange-600 transition-colors items-center gap-2 shadow-sm"
                    title="Powróć do konta super administratora"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    <span>Powrót</span>
                  </button>
                )}

                {/* Weather */}
                {weather && (
                  <div className="hidden md:flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-black/[0.04] shadow-card">
                    <WeatherIcon className="h-4 w-4 text-ink-soft" />
                    <span className="text-sm font-semibold text-ink">{weather.temp}°C</span>
                    <span className="text-xs text-ink-faint hidden lg:inline">{getWeatherLabel(weather.code)}</span>
                  </div>
                )}

                {/* Notifications */}
                <div className="relative" ref={notificationsRef}>
                  <button
                    type="button"
                    onClick={() => setNotificationsOpen(!notificationsOpen)}
                    className="relative p-2.5 rounded-xl bg-white border border-black/[0.04] shadow-card text-ink-soft hover:text-ink transition-colors"
                  >
                    <Bell className="h-5 w-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-accent-red text-white text-[10px] font-bold">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>

                  {notificationsOpen && (
                    <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-2xl shadow-card-hover border border-black/[0.06] z-50 max-h-96 overflow-hidden flex flex-col">
                      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-ink">Powiadomienia</h3>
                        {unreadCount > 0 && (
                          <button
                            onClick={() => markAllAsReadMutation.mutate()}
                            className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                          >
                            Oznacz wszystkie
                          </button>
                        )}
                      </div>
                      <div className="overflow-y-auto">
                        {notifications.length === 0 ? (
                          <div className="px-4 py-10 text-center text-ink-faint text-sm">
                            Brak powiadomień
                          </div>
                        ) : (
                          <div className="divide-y divide-gray-50">
                            {notifications.slice(0, 10).map((notification) => (
                              <div
                                key={notification.id}
                                onClick={() => handleNotificationClick(notification)}
                                className={`px-4 py-3 hover:bg-surface cursor-pointer transition-colors ${
                                  !notification.read ? 'bg-primary-50/60' : ''
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  <div className={`flex-shrink-0 w-2 h-2 rounded-full mt-2 ${
                                    notification.type === 'error' ? 'bg-red-500' :
                                    notification.type === 'warning' ? 'bg-amber-500' :
                                    notification.type === 'success' ? 'bg-emerald-500' :
                                    'bg-primary-500'
                                  }`} />
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-sm font-medium ${
                                      !notification.read ? 'text-ink' : 'text-ink-soft'
                                    }`}>
                                      {notification.title}
                                    </p>
                                    <p className="text-xs text-ink-faint mt-0.5 line-clamp-2">
                                      {notification.message}
                                    </p>
                                    <p className="text-[11px] text-gray-400 mt-1">
                                      {new Date(notification.timestamp).toLocaleString('pl-PL')}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* User pill */}
                <Link
                  to="/profile"
                  className="flex items-center gap-2.5 pl-1.5 pr-3 py-1.5 rounded-full bg-white border border-black/[0.04] shadow-card hover:shadow-card-hover transition-shadow"
                >
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold ${
                    isImpersonating ? 'bg-orange-500' : 'bg-primary-600'
                  }`}>
                    {initials}
                  </div>
                  <div className="hidden sm:block min-w-0">
                    <p className="text-sm font-semibold text-ink truncate max-w-[120px]">{displayName}</p>
                    <p className="text-[11px] text-ink-faint leading-none">{getRoleLabel(user?.role)}</p>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1">
          <div className="py-5 sm:py-6 print:py-0">
            <div className="w-full px-4 sm:px-5 lg:px-6 print:px-0">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

const SidebarContent = ({
  navigation,
  isCurrentPath,
  pathname = '',
  user,
  logout,
  isImpersonating,
  stopImpersonating,
  originalAdmin,
  plannerMenuOpen,
  setPlannerMenuOpen,
  usersMenuOpen,
  setUsersMenuOpen,
  kadryMenuOpen,
  setKadryMenuOpen,
  devicesMenuOpen,
  setDevicesMenuOpen,
  plannerMenuClosedRef,
  usersMenuClosedRef,
  kadryMenuClosedRef,
  devicesMenuClosedRef,
  displayName,
  initials
}) => (
  <div className="flex flex-col h-full">
    {/* Brand */}
    <div className="flex items-center gap-3 px-5 pt-6 pb-4 flex-shrink-0">
      <div className="w-10 h-10 rounded-xl overflow-hidden bg-white/5 flex items-center justify-center flex-shrink-0">
        <img 
          src={logoImage} 
          alt="Omega Operations" 
          className="w-9 h-9 object-contain"
        />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-bold text-white tracking-tight truncate">Omega Operations</p>
        <p className="text-[11px] text-sidebar-muted truncate">Farm Connect</p>
      </div>
    </div>

    <nav className="flex-1 px-3 pb-4 overflow-y-auto space-y-0.5">
      {navigation.filter(item => {
        if (item.superAdminOnly) {
          return !isImpersonating && user?.role === 'super admin';
        }
        if (item.adminAccess) {
          if (isImpersonating) {
            return user?.role === 'admin';
          }
          return user?.role === 'admin' || user?.role === 'super admin';
        }
        if (item.moduleAccess || item.moduleAccessAny) {
          if (user?.role === 'super admin' && !isImpersonating) {
            return true;
          }
          if (item.submenu && item.submenu.length > 0) {
            const hasAccessToSubmenu = item.submenu.some(subItem => {
              if (!subItem.moduleAccess && !subItem.moduleAccessAny) return true;
              if (subItem.moduleAccessAny) {
                return subItem.moduleAccessAny.some(m => user?.moduleAccess?.[m] === true);
              }
              return user?.moduleAccess?.[subItem.moduleAccess] === true;
            });
            return hasAccessToSubmenu;
          }
          if (item.moduleAccessAny) {
            return item.moduleAccessAny.some(m => user?.moduleAccess?.[m] === true);
          }
          return user?.moduleAccess?.[item.moduleAccess] === true;
        }
        return true;
      }).map((item) => {
        const Icon = item.icon;
        const hasSubmenu = item.submenu && item.submenu.length > 0;
        const isPlannerItem = item.name === 'Planer';
        const isUsersItem = item.name === 'Użytkownicy';
        const isKadryItem = item.name === 'Kadry';
        const isDevicesItem = item.name === 'Urządzenia';
        const isActive = hasSubmenu 
          ? item.submenu.some(subItem => isCurrentPath(subItem.href)) || isCurrentPath(item.href)
          : isCurrentPath(item.href);
        
        const menuOpen = isPlannerItem ? plannerMenuOpen : (isUsersItem ? usersMenuOpen : (isKadryItem ? kadryMenuOpen : (isDevicesItem ? devicesMenuOpen : false)));
        const setMenuOpen = isPlannerItem ? setPlannerMenuOpen : (isUsersItem ? setUsersMenuOpen : (isKadryItem ? setKadryMenuOpen : (isDevicesItem ? setDevicesMenuOpen : () => {})));

        return (
          <div key={item.name}>
            {hasSubmenu ? (
              <>
                <button
                  onClick={() => {
                    const newState = !menuOpen;
                    setMenuOpen(newState);
                    if (!newState) {
                      if (isPlannerItem) plannerMenuClosedRef.current = true;
                      if (isUsersItem) usersMenuClosedRef.current = true;
                      if (isKadryItem) kadryMenuClosedRef.current = true;
                      if (isDevicesItem) devicesMenuClosedRef.current = true;
                    } else {
                      if (isPlannerItem) plannerMenuClosedRef.current = false;
                      if (isUsersItem) usersMenuClosedRef.current = false;
                      if (isKadryItem) kadryMenuClosedRef.current = false;
                      if (isDevicesItem) devicesMenuClosedRef.current = false;
                    }
                  }}
                  className={`group nav-item ${isActive ? 'nav-item-active' : 'nav-item-idle'} justify-between`}
                >
                  <div className="flex items-center min-w-0">
                    <Icon className={`mr-3 flex-shrink-0 h-5 w-5 ${isActive ? 'text-accent-yellow' : 'text-sidebar-muted group-hover:text-gray-300'}`} />
                    <span className="truncate">{item.name}</span>
                  </div>
                  {menuOpen ? (
                    <ChevronDown className="h-4 w-4 text-sidebar-muted flex-shrink-0" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-sidebar-muted flex-shrink-0" />
                  )}
                </button>
                {menuOpen && (
                  <div className="ml-4 mt-0.5 mb-1 space-y-0.5 border-l border-white/10 pl-2">
                    {item.submenu.filter(subItem => {
                      if (subItem.moduleAccess || subItem.moduleAccessAny) {
                        if (user?.role === 'super admin' && !isImpersonating) {
                          return true;
                        }
                        if (subItem.kadryHROnly) {
                          return user?.department === 'HR' || user?.role === 'super admin';
                        }
                        if (subItem.moduleAccessAny) {
                          return subItem.moduleAccessAny.some(m => user?.moduleAccess?.[m] === true);
                        }
                        return user?.moduleAccess?.[subItem.moduleAccess] === true;
                      }
                      return true;
                    }).map((subItem) => {
                      const SubIcon = subItem.icon;
                      const isSubActive =
                        subItem.href === '/kadry/wnioski-delegacyjne'
                          ? pathname === '/kadry/wnioski-delegacyjne' ||
                            pathname.startsWith('/kadry/dokumenty/wniosek-delegacyjny')
                          : isCurrentPath(subItem.href);
                      return (
                        <Link
                          key={subItem.name}
                          to={subItem.href}
                          className={`group nav-item text-[13px] py-2 ${
                            isSubActive ? 'nav-item-active' : 'nav-item-idle'
                          }`}
                        >
                          <SubIcon
                            className={`mr-2.5 flex-shrink-0 h-4 w-4 ${
                              isSubActive ? 'text-accent-yellow' : 'text-sidebar-muted'
                            }`}
                          />
                          {subItem.name}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <Link
                to={item.href}
                className={`group nav-item ${
                  isCurrentPath(item.href) ? 'nav-item-active' : 'nav-item-idle'
                }`}
              >
                <Icon
                  className={`mr-3 flex-shrink-0 h-5 w-5 ${
                    isCurrentPath(item.href)
                      ? 'text-accent-yellow'
                      : item.name === 'Zgłoszenia'
                        ? 'text-accent-red'
                        : 'text-sidebar-muted group-hover:text-gray-300'
                  }`}
                />
                <span className="flex-1 truncate">{item.name}</span>
                {item.badge > 0 && (
                  <span className="ml-2 flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-accent-red text-white text-[10px] font-bold">
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </Link>
            )}
          </div>
        );
      })}
    </nav>

    {/* User footer */}
    <div className="flex-shrink-0 border-t border-white/10 p-4">
      {isImpersonating && (
        <div className="bg-orange-500/15 border border-orange-500/30 rounded-xl p-2.5 mb-3">
          <p className="text-[11px] font-medium text-orange-300 mb-1.5 truncate">
            Jako: {user?.username}
            {originalAdmin?.username ? ` ← ${originalAdmin.username}` : ''}
          </p>
          <button
            onClick={async () => {
              const result = await stopImpersonating();
              if (result.success) {
                window.location.href = '/dashboard';
              }
            }}
            className="w-full px-2 py-1.5 bg-orange-500 text-white text-xs font-semibold rounded-lg hover:bg-orange-600 transition-colors flex items-center justify-center gap-1.5"
          >
            <RotateCcw className="h-3 w-3" />
            <span>Powrót do super admina</span>
          </button>
        </div>
      )}
      <div className="flex items-center gap-3 min-w-0">
        <div className={`h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold ${
          isImpersonating ? 'bg-orange-500' : 'bg-primary-600'
        }`}>
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white truncate">{displayName}</p>
          <p className="text-[11px] text-sidebar-muted truncate">{user?.email}</p>
        </div>
        <button
          onClick={logout}
          className="flex-shrink-0 p-2 rounded-lg text-sidebar-muted hover:text-white hover:bg-white/10 transition-colors"
          title="Wyloguj"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </div>
  </div>
);

export default Layout;
